import { useEffect } from 'react';
import { useConsentStore } from './consentStore';
import { hasValidConsent } from './consentPolicy';
import { detectCountry, isInScopeCountry } from './geo';

// Module-level single-flight so the async geo lookup runs at most ONCE per page load (survives
// StrictMode's double-invoke of effects in dev). Re-initialised on a fresh load.
let _geoStarted = false;

/**
 * Mount ONCE at the app root. Non-blocking: it never gates render. If the user already decided under the
 * current policy version, it does nothing — no geo call, no banner (the "do it at most once" rule).
 * Otherwise it kicks the async country lookup and sets `scope`, which is what activates the banner (F3)
 * and the Hotjar gate (F2) when the result arrives. If geo can't be determined ⇒ out-of-scope.
 */
export function useConsentBootstrap(): void {
  const consent = useConsentStore((s) => s.consent);
  const setScope = useConsentStore((s) => s.setScope);

  useEffect(() => {
    if (hasValidConsent(consent)) return; // already chose → skip geo entirely
    if (_geoStarted) return;
    _geoStarted = true;
    void detectCountry().then((country) => {
      setScope(isInScopeCountry(country) ? 'in-scope' : 'out-of-scope');
    });
    // Intentionally run-once on mount; the stored-consent short-circuit above handles the decided case.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
